import useEmblaCarousel, { type UseEmblaCarouselType } from 'embla-carousel-react'
import { ArrowLeft, ArrowRight } from 'lucide-react'
import * as React from 'react'
import { useShadowHost } from '@homerunner-next/widget-core/runtime'

import { Button } from './button'
import { cn } from '../../lib/utils'

type CarouselApi = UseEmblaCarouselType[1]
type UseCarouselParameters = Parameters<typeof useEmblaCarousel>
type CarouselOptions = UseCarouselParameters[0]
type CarouselPlugin = UseCarouselParameters[1]

type CarouselProps = {
  opts?: CarouselOptions
  plugins?: CarouselPlugin
  orientation?: 'horizontal' | 'vertical'
  setApi?: (api: CarouselApi) => void
}

type CarouselContextProps = {
  carouselRef: ReturnType<typeof useEmblaCarousel>[0]
  api: ReturnType<typeof useEmblaCarousel>[1]
  direction: 'ltr' | 'rtl'
  scrollPrev: () => void
  scrollNext: () => void
  canScrollPrev: boolean
  canScrollNext: boolean
} & CarouselProps

const CarouselContext = React.createContext<CarouselContextProps | null>(null)

function useCarousel() {
  const context = React.useContext(CarouselContext)

  if (!context) {
    throw new Error('useCarousel must be used within a <Carousel />')
  }

  return context
}

const Carousel = React.forwardRef<HTMLDivElement, React.ComponentProps<'div'> & CarouselProps>(
  function Carousel(
    {
      orientation = 'horizontal',
      opts,
      setApi,
      plugins,
      className,
      children,
      ...props
    },
    forwardedRef
  ) {
    const rootRef = React.useRef<HTMLDivElement | null>(null)
    // The widget's shadow host, when shadow-mounted (null in dev/plain-DOM
    // mounts and in the SSR pass — same hook other widget code already uses
    // for light-DOM lookups, e.g. stickyElevation.ts, ProWhereField.tsx).
    // It's the widget's own custom element sitting in the HOST PAGE'S light
    // DOM, so walking its ancestors reaches the page's real `dir`.
    const shadowHost = useShadowHost()

    // Effective text direction for this instance. SSR always renders 'ltr'
    // (no DOM to inspect, and no render-context `dir` field exists to read
    // instead — see #712). The client reads the ACTUAL direction once after
    // mount, in three tiers: (1) the shadow host's own light-DOM ancestor
    // chain — covers every shadow-mounted widget regardless of what CSS the
    // shadow tree itself declares; (2) this element's own ancestor chain,
    // for a plain-DOM mount with no shadow host; (3) computed `direction`,
    // for a page that sets it via CSS rather than the `dir` attribute (also
    // the correct fallback for `dir`'s ordinary CSS-inheritance case).
    // Read once at mount, not watched live — no consumer needs a
    // mid-session `dir` flip today, and the codebase has no existing
    // pattern for one.
    const [direction, setDirection] = React.useState<'ltr' | 'rtl'>('ltr')
    React.useEffect(() => {
      const node = rootRef.current
      if (!node || typeof window === 'undefined') return
      const dirAncestor = shadowHost?.closest('[dir]') ?? node.closest('[dir]')
      const rtl = dirAncestor
        ? dirAncestor.getAttribute('dir') === 'rtl'
        : window.getComputedStyle(node).direction === 'rtl'
      if (rtl) setDirection('rtl')
    }, [shadowHost])

    const [carouselRef, api] = useEmblaCarousel(
      {
        direction,
        ...opts,
        axis: orientation === 'horizontal' ? 'x' : 'y',
      },
      plugins
    )
    const [canScrollPrev, setCanScrollPrev] = React.useState(false)
    const [canScrollNext, setCanScrollNext] = React.useState(false)

    const onSelect = React.useCallback((api: CarouselApi) => {
      if (!api) return
      setCanScrollPrev(api.canScrollPrev())
      setCanScrollNext(api.canScrollNext())
    }, [])

    const scrollPrev = React.useCallback(() => {
      api?.scrollPrev()
    }, [api])

    const scrollNext = React.useCallback(() => {
      api?.scrollNext()
    }, [api])

    // Physical keys move focus in the physical/visual direction, matching
    // the mirrored button positions and icons below: the Left key always
    // takes you to the slide now sitting on the physical left, whichever
    // logical direction (prev/next) that happens to be for the current
    // reading direction.
    const handleKeyDown = React.useCallback(
      (event: React.KeyboardEvent<HTMLDivElement>) => {
        if (event.key === 'ArrowLeft') {
          event.preventDefault()
          if (direction === 'rtl') scrollNext()
          else scrollPrev()
        } else if (event.key === 'ArrowRight') {
          event.preventDefault()
          if (direction === 'rtl') scrollPrev()
          else scrollNext()
        }
      },
      [scrollPrev, scrollNext, direction]
    )

    React.useEffect(() => {
      if (!api || !setApi) return
      setApi(api)
    }, [api, setApi])

    React.useEffect(() => {
      if (!api) return
      onSelect(api)
      api.on('reInit', onSelect)
      api.on('select', onSelect)

      return () => {
        api.off('reInit', onSelect)
        api.off('select', onSelect)
      }
    }, [api, onSelect])

    // Compose the refs so both Embla and the parent get the DOM node
    const composedRef = React.useCallback(
      (node: HTMLDivElement | null) => {
        rootRef.current = node
        if (typeof forwardedRef === 'function') {
          forwardedRef(node)
        } else if (forwardedRef) {
          (forwardedRef as React.MutableRefObject<HTMLDivElement | null>).current = node
        }
        if (carouselRef) {
          ((carouselRef as unknown) as React.MutableRefObject<HTMLDivElement | null>).current = node
        }
      },
      [forwardedRef, carouselRef]
    )

    return (
      <CarouselContext.Provider
        value={{
          carouselRef,
          api: api,
          opts,
          orientation: orientation || (opts?.axis === 'y' ? 'vertical' : 'horizontal'),
          direction,
          scrollPrev,
          scrollNext,
          canScrollPrev,
          canScrollNext,
        }}
      >
        <div
          ref={composedRef}
          onKeyDownCapture={handleKeyDown}
          className={cn('relative', className)}
          role="region"
          aria-roledescription="carousel"
          data-slot="carousel"
          // Only stamped for RTL — omitted entirely for LTR so the markup
          // stays byte-identical to before #712. Native browser bidi/drag
          // behavior stays consistent with the `direction: 'rtl'` Embla
          // option above (Embla's own recommendation for RTL carousels).
          dir={direction === 'rtl' ? 'rtl' : undefined}
          {...props}
        >
          {children}
        </div>
      </CarouselContext.Provider>
    )
  }
)

function CarouselContent({ className, ...props }: React.ComponentProps<'div'>) {
  const { carouselRef, orientation } = useCarousel()

  return (
    <div ref={carouselRef} className="overflow-hidden" data-slot="carousel-content">
      <div
        className={cn('flex', orientation === 'horizontal' ? '-ms-4' : '-mt-4 flex-col', className)}
        {...props}
      />
    </div>
  )
}

function CarouselItem({ className, ...props }: React.ComponentProps<'div'>) {
  const { orientation } = useCarousel()

  return (
    <div
      role="group"
      aria-roledescription="slide"
      data-slot="carousel-item"
      className={cn(
        'min-w-0 shrink-0 grow-0 basis-full',
        orientation === 'horizontal' ? 'ps-4' : 'pt-4',
        className
      )}
      {...props}
    />
  )
}

function CarouselPrevious({
  className,
  variant = 'outline',
  size = 'icon',
  ...props
}: React.ComponentProps<typeof Button>) {
  const { orientation, direction, scrollPrev, canScrollPrev } = useCarousel()
  // Mirror the icon in RTL so it still points toward where "previous"
  // content lives (the physical right, once the button itself has moved
  // there via the logical `-start-12` below). Vertical carousels are
  // unaffected by text direction — no swap there.
  const Icon = orientation === 'horizontal' && direction === 'rtl' ? ArrowRight : ArrowLeft

  return (
    <Button
      data-slot="carousel-previous"
      variant={variant}
      size={size}
      className={cn(
        'absolute size-8 rounded-full',
        orientation === 'horizontal'
          ? 'top-1/2 -start-12 -translate-y-1/2'
          : '-top-12 start-1/2 -translate-x-1/2 rotate-90',
        className
      )}
      disabled={!canScrollPrev}
      onClick={scrollPrev}
      {...props}
    >
      <Icon />
      <span className="sr-only">Previous slide</span>
    </Button>
  )
}

function CarouselNext({
  className,
  variant = 'outline',
  size = 'icon',
  ...props
}: React.ComponentProps<typeof Button>) {
  const { orientation, direction, scrollNext, canScrollNext } = useCarousel()
  // Mirror the icon in RTL so it still points toward where "next" content
  // lives (the physical left, once the button itself has moved there via
  // the logical `-end-12` below). Vertical carousels are unaffected by text
  // direction — no swap there.
  const Icon = orientation === 'horizontal' && direction === 'rtl' ? ArrowLeft : ArrowRight

  return (
    <Button
      data-slot="carousel-next"
      variant={variant}
      size={size}
      className={cn(
        'absolute size-8 rounded-full',
        orientation === 'horizontal'
          ? 'top-1/2 -end-12 -translate-y-1/2'
          : '-bottom-12 start-1/2 -translate-x-1/2 rotate-90',
        className
      )}
      disabled={!canScrollNext}
      onClick={scrollNext}
      {...props}
    >
      <Icon />
      <span className="sr-only">Next slide</span>
    </Button>
  )
}

// Dots component for carousel navigation
import { useEffect, useState, useCallback } from 'react'

function Dots() {
  const { api } = useCarousel()
  const [selectedIndex, setSelectedIndex] = useState(0)
  const [scrollSnaps, setScrollSnaps] = useState<number[]>([])

  const onDotButtonClick = useCallback(
    (index: number) => {
      if (!api) return
      api.scrollTo(index)
    },
    [api]
  )

  const onInit = useCallback((emblaApi: CarouselApi | undefined) => {
    if (!emblaApi) return
    setScrollSnaps(emblaApi.scrollSnapList())
  }, [])

  const onSelect = useCallback((emblaApi: CarouselApi | undefined) => {
    if (!emblaApi) return
    setSelectedIndex(emblaApi.selectedScrollSnap())
  }, [])

  useEffect(() => {
    if (!api) return
    onInit(api)
    onSelect(api)
    api.on('reInit', onInit).on('reInit', onSelect).on('select', onSelect)

    return () => {
      api.off('reInit', onInit).off('reInit', onSelect).off('select', onSelect)
    }
  }, [api, onInit, onSelect])

  return (
    <div className="flex justify-center mt-4 gap-3">
      {scrollSnaps.map((_, i) => (
        <button
          key={i}
          type="button"
          aria-label={`Go to slide ${i + 1}`}
          onClick={() => onDotButtonClick(i)}
          className={`w-3 h-3 rounded-full transition-all duration-200 focus:outline-none ${i === selectedIndex ? 'bg-foreground' : 'bg-muted'}`}
        />
      ))}
    </div>
  )
}

export { Carousel, CarouselContent, CarouselItem, CarouselNext, CarouselPrevious, Dots, type CarouselApi }
